/**
 * cart-items.js
 * Cart page: server-rendered items (Liquid). JS handles totals/badge/shipping bar only.
 *
 * Exposed as:  window.CartItems
 *
 * ── API surface used ──────────────────────────────────────────────────────
 *   GET  /cart.js  → fetch full cart on boot + on external cart:updated
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Depends on:  window.CartUtils  (cart-timer.js — optional; degrades gracefully)
 */

(function (global) {
  'use strict';

  /* ─── Live state ─────────────────────────────────────────────────────────── */
  var _items = [];
  var _totalPriceCents = 0;
  var _syncedOnce = false;   // true after the first applyCart (boot render)

  /* ─── DOM helpers ────────────────────────────────────────────────────────── */
  function _el(sel) { return document.querySelector(sel); }

  /* ─── Normalise a Shopify line item → internal shape ────────────────────── */
  function _normalise(item) {
    return {
      key:       item.key,
      qty:       item.quantity,
      price:     item.price / 100,
      finalLine: item.final_line_price,
      redeemed:  !!(item.properties && item.properties.redeemed),
    };
  }

  /* ─── Load cart from Shopify ─────────────────────────────────────────────── */
  function _loadCart(done) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      })
      .then(function (cart) { done(null, cart); })
      .catch(function (err) {
        console.error('[CartItems] _loadCart error:', err);
        done(err, null);
      });
  }

  /* ─── Apply a fresh Shopify cart response to local state & DOM ───────────── */
  function _applyCart(cart) {
    _items = cart.items.map(_normalise);
    _totalPriceCents = cart.total_price;

    // Keep subtotal data attributes in sync from the authoritative Shopify totals.
    // recalcTotalSavings() reads these to compute the displayed subtotal.
    // Shared writer (cart-timer.js) — also refreshes data-cart-redeemed-cents /
    // data-cart-points-alloc-cents, which cart-points.js reads for the redemption cap.
    var subtotalEl = _el('[data-cart-subtotal]');
    if (subtotalEl) {
      if (global.CartUtils && global.CartUtils.writeCartAttrs) {
        global.CartUtils.writeCartAttrs(cart);
      } else {
        subtotalEl.setAttribute('data-cart-subtotal-cents', cart.items_subtotal_price);
        subtotalEl.setAttribute('data-cart-total-cents',    cart.total_price);
        subtotalEl.setAttribute('data-cart-original-cents', cart.original_total_price);
      }
    }

    _render();
    // Skip the very first (boot) call — line rows are already server-rendered.
    // On every later refresh (discount code applied/removed, etc.) re-sync each
    // line's price from the authoritative cart so they don't go stale.
    if (_syncedOnce) _syncLineItemPrices(cart);
    _syncedOnce = true;
    // Notify other modules (header badge, etc.) — flag source to avoid loops
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { total_price: cart.total_price, item_count: cart.item_count, _source: 'CartItems' },
    }));
  }

  /* ─── Sync per-line prices from the authoritative cart ───────────────────── */
  // Cart-page line rows are server-rendered by Liquid and only updated live on
  // qty change (cart-layout.liquid). After a discount code is applied/removed the
  // line prices go stale, so re-sync them here — mirrors the qty-change logic
  // (uses item.line_price + $360.formatMoney with currency).
  function _syncLineItemPrices(cart) {
    if (!cart.items || !global.$360 || typeof global.$360.formatMoney !== 'function') return;
    // Scope to the cart-page list — the header drawer duplicates the
    // .cart-item[data-index] markup — and match by the stable line-item key
    // (falling back to position) so the correct row is updated.
    var itemsRoot = _el('[data-cart-items]');
    if (!itemsRoot) return;
    cart.items.forEach(function (item, idx) {
      var itemEl = (item.key && itemsRoot.querySelector('.cart-item[data-key="' + item.key + '"]'))
        || itemsRoot.querySelector('.cart-item[data-index="' + (idx + 1) + '"]');
      if (!itemEl) return;
      var priceDisplay = itemEl.querySelector('[data-price-display]');
      // if (priceDisplay) priceDisplay.textContent = global.$360.formatMoney(item.line_price, true);
      if (priceDisplay) priceDisplay.textContent = global.$360.formatMoney(item.original_line_price, true);
    });
  }

  /* ─── Render: markup is server-rendered by Liquid; sync derived UI only ──── */
  function _render() {
    var list   = _el('[data-cart-items]');
    if (!list) return;

    var fsBar  = _el('[data-free-shipping-bar]');
    var loader = _el('[data-cart-loading]');

    if (loader) loader.style.display = 'none';
    list.style.display = '';
    if (fsBar) fsBar.style.display = '';
    _syncTotals();
  }

  /* ─── Free-shipping bar ──────────────────────────────────────────────────── */
  function _syncFreeShippingBar(totalPrice) {
    var cfg       = global.cartConfig;
    var threshold = cfg && cfg.freeShippingEnabled && cfg.freeShippingThreshold
      ? cfg.freeShippingThreshold / 100   // cents → dollars
      : 0;

    if (threshold <= 0) return;

    var remaining = Math.max(0, threshold - totalPrice);
    var pct       = Math.min(100, (totalPrice / threshold) * 100);
    // Scope to cart-page-layout when present (header drawer has a duplicate
    // [data-fs-text]); fall back to document for non-cart-page contexts.
    var scope     = document.querySelector('.cart-page-layout') || document;
    var textEl    = scope.querySelector('[data-fs-text]');
    var iconEl    = scope.querySelector('[data-fs-icon]');
    var fillEl    = scope.querySelector('[data-fs-fill]');

    if (fillEl) {
      fillEl.style.width = pct.toFixed(1) + '%';
      fillEl.classList.toggle('is-full', remaining <= 0);
    }

    var badgeRect = scope.querySelector('[data-fs-badge-rect]');
    if (badgeRect) badgeRect.style.background = remaining > 0 ? '#E5E5E5' : '#F5B2D1';

    if (textEl) {
      if (global.$360FSMessage) global.$360FSMessage.update(textEl, remaining * 100);
      if (iconEl) iconEl.hidden = remaining > 0;
    }
  }

  /* ─── Sync all derived UI ────────────────────────────────────────────────── */
  function _syncTotals() {
    // Free-shipping eligibility = authoritative cart total (matches checkout) minus
    // points-redeemed items, whose value must not count toward the threshold.
    var totalQty = 0, redeemedFinalCents = 0;
    for (var i = 0; i < _items.length; i++) {
      totalQty += _items[i].qty;
      if (_items[i].redeemed) redeemedFinalCents += _items[i].finalLine;
    }
    var eligibleCents = Math.max(0, _totalPriceCents - redeemedFinalCents);

    // Cart count label
    var countEl = _el('[data-cart-count]');
    if (countEl) countEl.textContent = totalQty;

    _syncFreeShippingBar(eligibleCents / 100);

    // recalcTotalSavings reads data-cart-total-cents (set by _applyCart from the
    // authoritative Shopify response) and handles the subtotal text + savings row.
    if (global.CartUtils) {
      global.CartUtils.recalcTotalSavings();
    }
  }

  /* ─── External cart:updated listener ────────────────────────────────────── */
  // Handles updates from other page components (upsells, header mini-cart, etc.)
  document.addEventListener('cart:updated', function (e) {
    // Ignore events we dispatched ourselves to avoid an infinite reload loop
    if (e.detail && e.detail._source === 'CartItems') return;
    _loadCart(function (err, cart) { if (!err) _applyCart(cart); });
  });

  /* ─── Boot ───────────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    var list = _el('[data-cart-items]');
    if (!list) return;

    _loadCart(function (err, cart) {
      if (err) {
        console.error('[CartItems] failed to load cart on boot');
        return;
      }
      _applyCart(cart);
    });
  });

  /* ─── Public API ─────────────────────────────────────────────────────────── */
  global.CartItems = {
    render:    _render,
    applyCart: _applyCart,
    reload:    function () { _loadCart(function (err, cart) { if (!err) _applyCart(cart); }); },
  };

})(window);
